import React, { useState, useRef } from 'react';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { useAuth } from '../contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { Calendar, Download, Filter, FileText, Settings, Share2, Eye, Plus, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

const ReportGenerator = () => {
  const { analyticsData, generateReport, reports, loading } = useAnalytics();
  const { user } = useAuth();
  const [showBuilder, setShowBuilder] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  const reportRef = useRef();

  const [reportConfig, setReportConfig] = useState({
    title: '',
    description: '',
    type: 'comprehensive',
    dateRange: {
      start: format(subMonths(new Date(), 3), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd')
    },
    metrics: {
      goals: true,
      okrs: true,
      performance: true,
      trends: true
    },
    format: 'dashboard',
    schedule: 'manual',
    recipients: []
  });

  const reportTemplates = [
    {
      id: 'executive_summary',
      name: 'Executive Summary',
      description: 'High-level performance overview for leadership',
      metrics: { goals: true, okrs: true, performance: true, trends: false },
      icon: '📊'
    },
    {
      id: 'team_performance',
      name: 'Team Performance Report',
      description: 'Detailed team and individual performance analysis',
      metrics: { goals: true, okrs: true, performance: true, trends: true },
      icon: '👥'
    },
    {
      id: 'goal_tracking',
      name: 'Goal Tracking Report',
      description: 'Focus on goal completion and progress',
      metrics: { goals: true, okrs: false, performance: false, trends: true },
      icon: '🎯'
    },
    {
      id: 'okr_scorecard',
      name: 'OKR Scorecard',
      description: 'Comprehensive OKR performance analysis',
      metrics: { goals: false, okrs: true, performance: false, trends: true },
      icon: '🚀'
    },
    {
      id: 'quarterly_review',
      name: 'Quarterly Review',
      description: 'Complete quarterly performance assessment',
      metrics: { goals: true, okrs: true, performance: true, trends: true },
      icon: '📅'
    }
  ];

  const handleConfigChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setReportConfig(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setReportConfig(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleTemplateSelect = (template) => {
    setReportConfig(prev => ({
      ...prev,
      title: template.name,
      description: template.description,
      type: template.id,
      metrics: template.metrics
    }));
  };

  const handleGenerateReport = async () => {
    const report = await generateReport(reportConfig);
    if (report) {
      setPreviewReport(report);
      setShowBuilder(false);
    }
  };

  const exportToPDF = async () => {
    if (!reportRef.current) return;

    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${previewReport.title.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const exportToExcel = () => {
    if (!previewReport) return;

    const workbook = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ['Report Title', previewReport.title],
      ['Generated Date', format(previewReport.generatedAt, 'PPP')],
      ['Generated By', user?.first_name + ' ' + user?.last_name],
      [''],
      ['SUMMARY METRICS'],
      ['Goals Total', previewReport.data.summary.goals.total],
      ['Goals Completed', previewReport.data.summary.goals.completed],
      ['Goal Completion Rate', previewReport.data.summary.goals.completion_rate + '%'],
      ['OKR Total Objectives', previewReport.data.summary.okrs.total_objectives],
      ['OKR Average Score', Math.round(previewReport.data.summary.okrs.avg_score * 100) + '%'],
      ['Performance Reviews Total', previewReport.data.summary.performance.total_reviews],
      ['Performance Reviews Completed', previewReport.data.summary.performance.completed_reviews],
      ['Performance Average Score', Math.round(previewReport.data.summary.performance.avg_score * 100) + '%']
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Top Performers sheet
    if (previewReport.data.tables.topPerformers) {
      const performersData = [
        ['Name', 'Department', 'Goal Completion (%)', 'OKR Score (%)'],
        ...previewReport.data.tables.topPerformers.map(p => [
          p.name,
          p.department,
          p.goal_completion,
          Math.round(p.okr_score * 100)
        ])
      ];
      const performersSheet = XLSX.utils.aoa_to_sheet(performersData);
      XLSX.utils.book_append_sheet(workbook, performersSheet, 'Top Performers');
    }

    XLSX.writeFile(workbook, `${previewReport.title.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const exportToCSV = () => {
    if (!previewReport) return;

    const csvData = [
      ['Metric', 'Value'],
      ['Goals Total', previewReport.data.summary.goals.total],
      ['Goals Completed', previewReport.data.summary.goals.completed],
      ['Goal Completion Rate (%)', previewReport.data.summary.goals.completion_rate],
      ['OKR Total Objectives', previewReport.data.summary.okrs.total_objectives],
      ['OKR Average Score (%)', Math.round(previewReport.data.summary.okrs.avg_score * 100)],
      ['Performance Reviews Total', previewReport.data.summary.performance.total_reviews],
      ['Performance Reviews Completed', previewReport.data.summary.performance.completed_reviews],
      ['Performance Average Score (%)', Math.round(previewReport.data.summary.performance.avg_score * 100)]
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${previewReport.title.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
  };

  const ReportPreview = ({ report }) => (
    <div ref={reportRef} className="bg-white p-8 max-w-4xl mx-auto">
      {/* Report Header */}
      <div className="border-b pb-6 mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{report.title}</h1>
        <p className="text-gray-600 mb-4">{report.description}</p>
        <div className="flex justify-between text-sm text-gray-500">
          <span>Generated on {format(report.generatedAt, 'PPP')}</span>
          <span>By {user?.first_name} {user?.last_name}</span>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Executive Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Goals Performance</h3>
            <div className="text-2xl font-bold text-blue-600">
              {report.data.summary.goals.completion_rate}%
            </div>
            <p className="text-sm text-blue-700">
              {report.data.summary.goals.completed} of {report.data.summary.goals.total} completed
            </p>
          </div>
          
          <div className="bg-indigo-50 p-4 rounded-lg">
            <h3 className="font-medium text-indigo-900 mb-2">OKR Performance</h3>
            <div className="text-2xl font-bold text-indigo-600">
              {Math.round(report.data.summary.okrs.avg_score * 100)}%
            </div>
            <p className="text-sm text-indigo-700">
              {report.data.summary.okrs.total_objectives} active objectives
            </p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="font-medium text-green-900 mb-2">Review Completion</h3>
            <div className="text-2xl font-bold text-green-600">
              {report.data.summary.performance.completion_rate}%
            </div>
            <p className="text-sm text-green-700">
              {report.data.summary.performance.completed_reviews} of {report.data.summary.performance.total_reviews} completed
            </p>
          </div>
        </div>
      </div>

      {/* Charts */}
      {report.data.charts && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Performance Trends</h2>
          
          {/* Goal Trends Chart */}
          {report.data.charts.goalTrends && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Goal Completion Trends</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={report.data.charts.goalTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="completed" stroke="#3B82F6" name="Completed Goals" />
                  <Line type="monotone" dataKey="total" stroke="#6B7280" name="Total Goals" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* OKR Distribution Chart */}
          {report.data.charts.okrDistribution && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">OKR Score Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={report.data.charts.okrDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.range}: ${entry.count}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {report.data.charts.okrDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Top Performers Table */}
      {report.data.tables.topPerformers && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Top Performers</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Goal Completion</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OKR Score</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {report.data.tables.topPerformers.map((performer, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{performer.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{performer.department}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{performer.goal_completion}%</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Math.round(performer.okr_score * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Key Insights */}
      {report.data.insights && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Key Insights</h2>
          <div className="space-y-4">
            {report.data.insights.map((insight, index) => (
              <div key={index} className={`p-4 rounded-lg ${
                insight.type === 'positive' ? 'bg-green-50 border-l-4 border-green-400' :
                insight.type === 'warning' ? 'bg-yellow-50 border-l-4 border-yellow-400' :
                'bg-blue-50 border-l-4 border-blue-400'
              }`}>
                <h3 className="font-medium text-gray-900">{insight.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{insight.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <FileText className="mr-3 text-blue-600" size={28} />
            Report Generator
          </h2>
          <p className="text-gray-600 mt-1">Create custom performance reports and analytics</p>
        </div>
        
        <button
          onClick={() => setShowBuilder(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus className="mr-2" size={18} />
          New Report
        </button>
      </div>

      {/* Report Templates */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Report Templates</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportTemplates.map((template) => (
            <motion.button
              key={template.id}
              onClick={() => {
                handleTemplateSelect(template);
                setShowBuilder(true);
              }}
              className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-start">
                <span className="text-2xl mr-3">{template.icon}</span>
                <div>
                  <h4 className="font-medium text-gray-900">{template.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Recent Reports */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Reports</h3>
        {reports.length > 0 ? (
          <div className="space-y-3">
            {reports.slice(0, 5).map((report) => (
              <div key={report.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{report.title}</h4>
                  <p className="text-sm text-gray-600">{format(report.generatedAt, 'PPP')}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPreviewReport(report)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <Eye size={16} />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600">
                    <Download size={16} />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600">
                    <Share2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No reports generated yet. Create your first report above.</p>
        )}
      </div>

      {/* Report Builder Modal */}
      <AnimatePresence>
        {showBuilder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Report Builder</h3>
                  <button
                    onClick={() => setShowBuilder(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Basic Info */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Report Title</label>
                    <input
                      type="text"
                      value={reportConfig.title}
                      onChange={(e) => handleConfigChange('title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter report title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={reportConfig.description}
                      onChange={(e) => handleConfigChange('description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Describe what this report covers"
                    />
                  </div>

                  {/* Date Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="date"
                        value={reportConfig.dateRange.start}
                        onChange={(e) => handleConfigChange('dateRange.start', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="date"
                        value={reportConfig.dateRange.end}
                        onChange={(e) => handleConfigChange('dateRange.end', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Metrics Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Include Metrics</label>
                    <div className="space-y-2">
                      {Object.entries(reportConfig.metrics).map(([metric, enabled]) => (
                        <label key={metric} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => handleConfigChange(`metrics.${metric}`, e.target.checked)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 capitalize">{metric}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowBuilder(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleGenerateReport}
                      disabled={loading || !reportConfig.title}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loading ? 'Generating...' : 'Generate Report'}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Report Preview Modal */}
      <AnimatePresence>
        {previewReport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-screen overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Report Preview</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={exportToPDF}
                    className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    PDF
                  </button>
                  <button
                    onClick={exportToExcel}
                    className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Excel
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    CSV
                  </button>
                  <button
                    onClick={() => setPreviewReport(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              <ReportPreview report={previewReport} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReportGenerator;